import React, { useEffect, useMemo, useRef, useState } from "react";
import { COMBO_MS, FX_MS } from "../../services/gift";

/**
 * เอฟเฟกต์ส่งของขวัญแบบ TikTok บนหน้าแชท (CSS ล้วน ใช้แค่ transform/opacity ไม่มีไลบรารี)
 *  small  : ไอคอนลอยขึ้นจากด้านล่าง + หัวใจลอย
 *  medium : ไอคอนใหญ่เด้งกลางจอ + แสงวาบ + confetti
 *  big    : เต็มจอ พื้นหลังมืดลง ไอคอนบินเข้าพร้อมหมุน + เรืองแสง + ชื่อ "ส่ง xxx ให้ <ตัวละคร>"
 *  ทุกระดับ: แบนเนอร์เลื่อนเข้าด้านข้าง "ICE ส่ง กุหลาบ x3" + ตัวนับคอมโบ + ปุ่ม "ส่งอีก" (ภายใน 3 วินาที) ; กดแบนเนอร์/ข้าม/Esc = ปิด
 *  prefers-reduced-motion = แบนเนอร์เรียบ ๆ อย่างเดียว ; ชั้นนี้ไม่รับ pointer (ยกเว้นปุ่ม) และอยู่ใต้ช่องพิมพ์ จึงไม่บังปุ่มส่ง
 * event = {seq, tier, icon, name, combo, cost, sender, charName, giftId}
 */
const COLORS = ["#fbbf24", "#ec4899", "#8b5cf6", "#34d399", "#60a5fa", "#fb7185"];
const HEARTS = ["❤️", "💖", "💗", "💕"];

export function useReducedMotion() {
  const [r, setR] = useState(() => typeof window !== "undefined" && !!window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches);
  useEffect(() => {
    if (!window.matchMedia) return undefined;
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const on = () => setR(mq.matches);
    mq.addEventListener?.("change", on);
    return () => mq.removeEventListener?.("change", on);
  }, []);
  return r;
}

const rnd = (a, b) => a + Math.random() * (b - a);

function Confetti({ n }) {
  const pieces = useMemo(() => Array.from({ length: n }, (_, i) => {
    const ang = rnd(0, Math.PI * 2), dist = rnd(90, 230);
    return { i, dx: Math.round(Math.cos(ang) * dist), dy: Math.round(Math.sin(ang) * dist * 0.9 + 70), rot: Math.round(rnd(-540, 540)), delay: Math.round(rnd(0, 220)), c: COLORS[i % COLORS.length], w: Math.round(rnd(6, 11)) };
  }), [n]);
  return pieces.map((p) => (
    <i key={p.i} className="gfx-conf" style={{ "--dx": `${p.dx}px`, "--dy": `${p.dy}px`, "--rot": `${p.rot}deg`, animationDelay: `${p.delay}ms`, background: p.c, width: p.w, height: Math.round(p.w * 1.6) }} />
  ));
}

function Hearts({ n }) {
  const hs = useMemo(() => Array.from({ length: n }, (_, i) => ({ i, x: Math.round(rnd(-46, 46)), d: Math.round(rnd(0, 500)), s: rnd(0.8, 1.4).toFixed(2), e: HEARTS[i % HEARTS.length] })), [n]);
  return hs.map((h) => <span key={h.i} className="gfx-heart" style={{ "--x": `${h.x}px`, "--s": h.s, animationDelay: `${h.d}ms` }}>{h.e}</span>);
}

export default function GiftFx({ event, onResend, resending }) {
  const reduced = useReducedMotion();
  const [showFx, setShowFx] = useState(false);
  const [showAgain, setShowAgain] = useState(false);
  const layer = useRef(null);

  useEffect(() => {
    if (!event) return undefined;
    setShowFx(true);
    setShowAgain(true);
    const t1 = setTimeout(() => setShowFx(false), reduced ? 2200 : FX_MS[event.tier] || FX_MS.small);
    const t2 = setTimeout(() => setShowAgain(false), COMBO_MS);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [event, reduced]);

  // ชั้นเอฟเฟกต์คลุมทั้งห้อง แต่เนื้อหาอยู่เหนือช่องพิมพ์ (วัดความสูงช่องพิมพ์) จึงไม่บังปุ่มส่ง
  useEffect(() => {
    const el = layer.current;
    const comp = el && el.parentElement ? el.parentElement.querySelector(".composer") : null;
    if (el) el.style.setProperty("--gfx-bottom", `${comp ? comp.offsetHeight : 0}px`);
  });

  useEffect(() => {
    if (!showFx) return undefined;
    const onKey = (e) => { if (e.key === "Escape") setShowFx(false); };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [showFx]);

  if (!event || (!showFx && !showAgain)) return null;
  const { tier, icon, name, combo, cost, sender, charName } = event;
  const lite = combo > 1; // คอมโบ: ไม่ทำฉากใหญ่ซ้ำ เด้งตัวนับ + ไอคอนพอ (ลื่นตอนกดรัว)
  return (
    <div ref={layer} className={`gfx gfx--${tier}${reduced ? " gfx--reduced" : ""}`} data-testid="gift-fx" data-tier={tier} data-combo={combo}>
      {showFx ? (
        <>
          <div className="gfx-live" role="status">{`${sender} ส่ง ${name}${combo > 1 ? ` x${combo}` : ""}`}</div>
          {!reduced ? (
            <div className="gfx-stage" aria-hidden="true" key={event.seq}>
              {tier === "big" && !lite ? <div className="gfx-dim" /> : null}
              <div className="gfx-area">
              {tier === "small" ? (
                <div className="gfx-rise"><span className="gfx-rise__ico">{icon}</span><Hearts n={lite ? 3 : 6} /></div>
              ) : null}
              {tier === "medium" ? (
                <>
                  <div className="gfx-flash" />
                  <div className="gfx-pop"><span className="gfx-pop__ico">{icon}</span></div>
                  <Confetti n={lite ? 10 : 26} />
                </>
              ) : null}
              {tier === "big" ? (
                <>
                  <div className="gfx-halo" />
                  <div className="gfx-fly"><span className="gfx-fly__ico">{icon}</span></div>
                  {!lite ? <div className="gfx-title"><span className="gfx-title__who">{sender}</span><span className="gfx-title__what">ส่ง {name} ให้ {charName}</span></div> : null}
                  <Confetti n={lite ? 12 : 40} />
                </>
              ) : null}
                {combo > 1 && tier !== "small" ? <div className="gfx-count" key={combo}>x{combo}</div> : null}
              </div>
            </div>
          ) : null}
          <button type="button" className="gfx-banner" onClick={() => setShowFx(false)} aria-label={`ปิดเอฟเฟกต์ ${sender} ส่ง ${name}${combo > 1 ? ` x${combo}` : ""}`}>
            <span className="gfx-banner__ico" aria-hidden="true">{icon}</span>
            <span className="gfx-banner__txt"><b>{sender}</b> ส่ง {name}</span>
            {combo > 1 ? <span className="gfx-banner__combo" key={combo}>x{combo}</span> : null}
          </button>
          {!reduced && tier !== "small" ? <button type="button" className="gfx-skip" onClick={() => setShowFx(false)}>ข้าม</button> : null}
        </>
      ) : null}
      {showAgain ? (
        <button type="button" className="gfx-again" key={event.seq} disabled={resending} onClick={() => onResend?.(event)} aria-label={`ส่ง ${name} อีกชิ้น ใช้ ${cost} คอยน์`}>
          <span aria-hidden="true">{icon}</span> ส่งอีก <small>{cost}</small>
        </button>
      ) : null}
    </div>
  );
}
